import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import {
  ArrowRight, ChevronLeft, Trophy, Clock, Target, Sparkles, Award,
  ChevronDown,
} from "lucide-react";

export default function ChallengeDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await apiClient.get(`/seo/challenge/${slug}`);
        setChallenge(r.data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useSEO({
    title: challenge ? `${challenge.h1} — Free AI Challenge · PromptDojo` : "Challenge · PromptDojo",
    description: challenge?.intro,
    canonical: `https://aipromptdojo.com/challenges/${slug}`,
    ogImage: "https://aipromptdojo.com/api/og/UNKNOWN.png",
    jsonLd: challenge
      ? {
          "@context": "https://schema.org",
          "@type": "LearningResource",
          name: challenge.h1,
          description: challenge.intro,
          educationalLevel: challenge.difficulty,
          timeRequired: `PT${challenge.estimated_minutes}M`,
          provider: { "@type": "Organization", name: "PromptDojo", sameAs: "https://aipromptdojo.com" },
          about: challenge.topic_name,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: (challenge.faq || []).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        }
      : null,
  });

  if (notFound) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="font-display text-4xl">Challenge not found</h1>
          <Link to="/topics" className="btn-primary mt-6 inline-flex" data-testid="challenge-back-to-topics">
            Back to all topics
          </Link>
        </main>
      </div>
    );
  }
  if (loading || !challenge) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto p-20 text-center"><span className="spinner" /></main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 pb-28 md:pb-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[var(--ink-3)] mb-6 flex-wrap" aria-label="breadcrumb">
          <Link to="/topics" className="hover:text-[var(--ink-2)]">Topics</Link>
          <span>/</span>
          <Link to={`/topics/${challenge.topic_slug}`} className="hover:text-[var(--ink-2)]">{challenge.topic_name}</Link>
          <span>/</span>
          <span className="text-[var(--ink-2)]">{challenge.subtopic_name}</span>
        </nav>

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: challenge.accent }}
          >
            <span className="text-2xl leading-none">{challenge.emoji}</span>
          </div>
          <span className="kicker" style={{ color: challenge.accent }}>Challenge</span>
        </div>

        <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
          {challenge.h1}
        </h1>
        <p className="text-[var(--ink-2)] mt-4 leading-relaxed text-lg">{challenge.intro}</p>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          <Stat icon={Award} label="Difficulty" value={challenge.difficulty} accent={challenge.accent} />
          <Stat icon={Trophy} label="Reward" value="Belt progress" accent={challenge.accent} />
          <Stat icon={Clock} label="Time" value={`${challenge.estimated_minutes} min`} accent={challenge.accent} />
        </div>

        {/* Primary CTA — sticky-ish */}
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/" className="btn-duo" data-testid="challenge-cta-start">
            Start challenge free <ArrowRight size={14} />
          </Link>
          <Link to="/topics" className="btn-ghost" data-testid="challenge-cta-browse">
            Browse all challenges
          </Link>
        </div>

        {/* Description */}
        <section className="mt-12">
          <h2 className="font-display text-2xl leading-tight mb-3">What you'll do</h2>
          <p className="text-[var(--ink-2)] leading-relaxed">{challenge.description}</p>
        </section>

        {/* Learning objectives */}
        <section className="mt-10">
          <h2 className="font-display text-2xl leading-tight mb-3">Learning objectives</h2>
          <ul className="space-y-2">
            {(challenge.objectives || []).map((o, i) => (
              <li key={`obj-${o}`} className="flex items-start gap-3 text-[var(--ink)]">
                <span
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5"
                  style={{ background: challenge.accent, color: "#fff" }}
                >
                  {i + 1}
                </span>
                <span className="leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Example prompt */}
        <section className="mt-10">
          <h2 className="font-display text-2xl leading-tight mb-3 flex items-center gap-2">
            <Target size={20} style={{ color: challenge.accent }} /> Example prompt
          </h2>
          <pre
            className="brut p-5 font-mono text-sm leading-relaxed text-[var(--ink)] whitespace-pre-wrap"
            data-testid="challenge-example-prompt"
          >
            {challenge.example_prompt}
          </pre>
        </section>

        {/* Example output */}
        <section className="mt-10">
          <h2 className="font-display text-2xl leading-tight mb-3 flex items-center gap-2">
            <Sparkles size={20} style={{ color: challenge.accent }} /> How your AI tutor responds
          </h2>
          <pre
            className="brut p-5 font-mono text-sm leading-relaxed text-[var(--ink-2)] whitespace-pre-wrap"
            data-testid="challenge-example-output"
          >
            {challenge.example_output}
          </pre>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="font-display text-2xl leading-tight mb-4">Frequently asked questions</h2>
          <div className="space-y-2" data-testid="challenge-faq">
            {(challenge.faq || []).map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className="brut">
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left"
                    data-testid={`challenge-faq-${i}`}
                  >
                    <span className="font-medium text-[var(--ink)]">{f.q}</span>
                    <ChevronDown
                      size={16}
                      className="text-[var(--ink-3)] transition-transform flex-shrink-0"
                      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
                    />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-sm text-[var(--ink-2)] leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Related challenges */}
        {challenge.related?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl leading-tight mb-4">Related challenges</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {challenge.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/challenges/${r.slug}`}
                  className="brut p-4 group transition-all hover:-translate-y-0.5"
                  data-testid={`challenge-related-${r.slug}`}
                >
                  <div className="font-display text-base leading-tight">{r.h1}</div>
                  <div className="text-xs text-[var(--ink-3)] mt-1.5 flex items-center gap-3">
                    <span>{r.difficulty}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final conversion CTA */}
        <div
          className="mt-14 brut-lg p-8 md:p-10 text-center"
          style={{ background: `linear-gradient(135deg, ${challenge.accent}1f 0%, ${challenge.accent}05 100%)`, borderColor: `${challenge.accent}55` }}
        >
          <span className="kicker" style={{ color: challenge.accent }}>Begin your training</span>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mt-2">
            Complete the challenge. Advance your belt.
          </h2>
          <p className="text-[var(--ink-2)] mt-3 max-w-lg mx-auto">
            One short prompt, graded by Claude. Find your level first, then start training.
          </p>
          <Link to="/skill-check" className="btn-duo mt-6 inline-flex" data-testid="challenge-cta-bottom">
            Start My Skill Check <ArrowRight size={14} />
          </Link>
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="brut p-4 text-center">
      <Icon size={14} className="mx-auto mb-1.5" style={{ color: accent }} />
      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ink-3)] font-medium">{label}</div>
      <div className="font-display text-base mt-1 leading-tight">{value}</div>
    </div>
  );
}
